import { useCallback, useEffect, useMemo, useState } from 'react';

import type { AdminUserManagementOrganizationRecord } from '@cartwhl/common/api/getAdminUserManagementDetail';
import Input from '@cartwhl/ui/atoms/Input';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { useStore } from '../adminUserManagementStore';

export default function AdminUserManagement() {
  const store = useStore();
  const {
    searchQuery,
    searchLoading,
    searchError,
    viewMode,
    userDetail,
    organizationDetail,
    detailLoading,
    detailError,
    actionLoading,
    manualOrganizations,
    manualOrganizationsLoading,
    manualOrganizationsError,
  } = store.state;

  useEffect(() => {
    store.loadManualOrganizations();
  }, [store]);

  const [orgName, setOrgName] = useState('');
  const [addToOrgId, setAddToOrgId] = useState('');
  const [addToOrgPlan, setAddToOrgPlan] = useState<
    'free' | 'pro' | 'unlimited'
  >('pro');
  const [addToOrgAccess, setAddToOrgAccess] = useState<'EDIT' | 'VIEW'>('VIEW');
  const [serviceName, setServiceName] = useState('');
  const [balanceDeltas, setBalanceDeltas] = useState<Record<string, string>>(
    {},
  );
  const [invalidAfterDates, setInvalidAfterDates] = useState<
    Record<string, string>
  >({});
  const [memberRemovePrompt, setMemberRemovePrompt] = useState<{
    userID: string;
    email: string;
    displayLabel: string;
  } | null>(null);
  const [addSeatPlan, setAddSeatPlan] = useState<'pro' | 'unlimited' | 'team'>(
    'pro',
  );
  const [addSeatCount, setAddSeatCount] = useState('');
  const [orgPageAddMemberTarget, setOrgPageAddMemberTarget] = useState('');
  const [recentOrgsFilter, setRecentOrgsFilter] = useState('');
  const [manualOrgCreditsToGrant, setManualOrgCreditsToGrant] = useState('');

  const filteredRecentOrganizations = useMemo(() => {
    if (!manualOrganizations) return null;
    const q = recentOrgsFilter.trim().toLowerCase();
    if (!q) return manualOrganizations;
    return manualOrganizations.filter(
      (o) =>
        o.organizationName.toLowerCase().includes(q) ||
        o.organizationID.toLowerCase().includes(q),
    );
  }, [manualOrganizations, recentOrgsFilter]);

  const handleSearch = useCallback(() => {
    store.search();
  }, [store]);

  const handleViewUser = useCallback(
    (id: string) => {
      store.loadUserDetail(id);
    },
    [store],
  );

  const handleViewOrganization = useCallback(
    (id: string) => {
      store.loadOrganizationDetail(id);
    },
    [store],
  );

  const handleCreateOrgForUser = useCallback(
    async (creatorUserID: string) => {
      const name = orgName.trim() || 'New Organization';
      const orgId = await store.createOrganization(name, creatorUserID);
      if (orgId) {
        setOrgName('');
        store.loadOrganizationDetail(orgId);
      }
    },
    [store, orgName],
  );

  const handleAddUserToOrg = useCallback(
    async (targetUserID: string) => {
      const orgId = addToOrgId.trim();
      if (!orgId) return;
      const ok = await store.addUserToOrganization(
        orgId,
        targetUserID,
        addToOrgPlan,
        addToOrgAccess,
      );
      if (ok) {
        setAddToOrgId('');
        store.loadOrganizationDetail(orgId);
      }
    },
    [store, addToOrgId, addToOrgPlan, addToOrgAccess],
  );

  const handleAddUserToCurrentOrg = useCallback(async () => {
    if (!organizationDetail) return;
    const orgId = (
      organizationDetail.organization as { organizationID?: string }
    )?.organizationID;
    const target = orgPageAddMemberTarget.trim();
    if (!orgId || !target) return;
    const ok = await store.addUserToOrganization(
      orgId,
      target,
      addToOrgPlan,
      addToOrgAccess,
    );
    if (ok) {
      setOrgPageAddMemberTarget('');
      await store.loadOrganizationDetail(orgId);
    }
  }, [
    store,
    organizationDetail,
    orgPageAddMemberTarget,
    addToOrgPlan,
    addToOrgAccess,
  ]);

  const handleConfirmRemoveMember = useCallback(async () => {
    if (!memberRemovePrompt || !organizationDetail) return;
    const orgId = (
      organizationDetail.organization as { organizationID?: string }
    )?.organizationID;
    if (!orgId) return;
    const target = memberRemovePrompt.email.trim() || memberRemovePrompt.userID;
    const ok = await store.removeUserFromOrganization(orgId, target);
    if (ok) {
      setMemberRemovePrompt(null);
      await store.loadOrganizationDetail(orgId);
    }
  }, [store, memberRemovePrompt, organizationDetail]);

  const handleModifyOrganizationSeatCapacity = useCallback(async () => {
    if (!organizationDetail) return;
    const orgId = (
      organizationDetail.organization as { organizationID?: string }
    )?.organizationID;
    if (!orgId) return;
    const n = parseInt(addSeatCount, 10);
    const ok = await store.modifyOrganizationSeatCapacity(
      orgId,
      addSeatPlan,
      n,
    );
    if (ok) {
      setAddSeatCount('');
      await store.loadOrganizationDetail(orgId);
    }
  }, [store, organizationDetail, addSeatCount, addSeatPlan]);

  const handleCreateServiceAccount = useCallback(
    async (organizationID: string) => {
      const name = serviceName.trim() || 'API Key';
      const result = await store.createServiceAccount(organizationID, name);
      if (result) {
        setServiceName('');
        store.loadOrganizationDetail(organizationID);
      }
    },
    [store, serviceName],
  );

  const handleDeleteServiceAccount = useCallback(
    async (organizationID: string, serviceID: string) => {
      if (
        !window.confirm(
          `Delete API key ${serviceID}? This cannot be undone and will revoke the key immediately.`,
        )
      ) {
        return;
      }
      await store.deleteServiceAccount(organizationID, serviceID);
    },
    [store],
  );

  const handleGrantManualOrgCredits = useCallback(
    async (organizationID: string) => {
      const n = parseInt(manualOrgCreditsToGrant, 10);
      const ok = await store.grantManualOrgCredits(organizationID, n);
      if (ok) {
        setManualOrgCreditsToGrant('');
      }
    },
    [store, manualOrgCreditsToGrant],
  );

  const formatMoney = (n: number | undefined, decimals: number) =>
    n == null || Number.isNaN(n) ? '—' : `$${n.toFixed(decimals)}`;

  const handleUpdateServiceAccount = useCallback(
    async (
      serviceID: string,
      organizationID: string,
      balanceDelta?: number,
      invalidAfter?: string,
    ) => {
      const updates: {
        accountBalanceDeltaUSD?: number;
        invalidAfter?: string;
      } = {};
      if (balanceDelta !== undefined && balanceDelta !== 0) {
        updates.accountBalanceDeltaUSD = balanceDelta;
      }
      if (invalidAfter) {
        updates.invalidAfter = new Date(invalidAfter).toISOString();
      }
      if (Object.keys(updates).length === 0) return;
      const ok = await store.updateServiceAccount(
        serviceID,
        organizationID,
        updates,
      );
      if (ok) {
        setBalanceDeltas((prev) => ({ ...prev, [serviceID]: '' }));
        setInvalidAfterDates((prev) => ({ ...prev, [serviceID]: '' }));
      }
    },
    [store],
  );

  const searchBar = (
    <div className="flex gap-2">
      <Input
        name="searchQuery"
        placeholder="Email, user-xxx, organization-xxx, or service-xxx"
        value={searchQuery}
        onChange={(e) => store.setSearchQuery(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
      />
      <Button disabled={searchLoading} onClick={handleSearch}>
        Search
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="container mx-auto py-12">
        <div className="mb-8 flex flex-col gap-2">
          <Text
            variant="studio/display/xl"
            className="font-display text-primary text-[40px] leading-[48px]"
            as="h1"
          >
            Admin User Management
          </Text>
          <Text variant="studio/body/medium/24" className="text-tertiary">
            Search by email, user ID, organization ID, or service ID.
          </Text>
        </div>

        {searchBar}

        {viewMode === 'search' && (
          <>
            {searchLoading && (
              <div className="mt-8 flex justify-center">
                <Loading className="h-10 w-10" />
              </div>
            )}
            {searchError && (
              <Text className="mt-4 text-red-600" variant="studio/body/md">
                {searchError}
              </Text>
            )}
            <div className="mt-8">
              <Text as="h2" variant="studio/body/bold/md" className="mb-4">
                Recent organizations
              </Text>
              <div className="mb-4 max-w-md">
                <Input
                  name="recentOrgsFilter"
                  placeholder="Filter by name or organization ID"
                  value={recentOrgsFilter}
                  onChange={(e) => setRecentOrgsFilter(e.target.value)}
                />
              </div>
              {manualOrganizationsLoading && (
                <div className="flex justify-center py-8">
                  <Loading className="h-8 w-8" />
                </div>
              )}
              {manualOrganizationsError && (
                <Text className="text-red-600" variant="studio/body/md">
                  {manualOrganizationsError}
                </Text>
              )}
              {filteredRecentOrganizations &&
                filteredRecentOrganizations.length > 0 && (
                  <div className="rounded-lg border">
                    {filteredRecentOrganizations.map((org) => (
                      <div
                        key={org.organizationID}
                        className="flex items-center justify-between border-b p-3 last:border-b-0"
                      >
                        <div>
                          <Text as="span" variant="studio/body/md">
                            {org.organizationName}
                          </Text>
                          <Text
                            as="span"
                            variant="studio/body/sm"
                            className="text-tertiary ml-2"
                          >
                            ({org.organizationID})
                          </Text>
                          {org.createdAt && (
                            <Text
                              as="span"
                              variant="studio/body/sm"
                              className="text-tertiary ml-2 block"
                            >
                              Created {new Date(org.createdAt).toLocaleString()}
                            </Text>
                          )}
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() =>
                            handleViewOrganization(org.organizationID)
                          }
                        >
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              {manualOrganizations &&
                !manualOrganizationsLoading &&
                manualOrganizations.length > 0 &&
                filteredRecentOrganizations &&
                filteredRecentOrganizations.length === 0 && (
                  <Text variant="studio/body/sm" className="text-tertiary">
                    No organizations match this filter.
                  </Text>
                )}
              {manualOrganizations &&
                !manualOrganizationsLoading &&
                manualOrganizations.length === 0 && (
                  <Text variant="studio/body/sm" className="text-tertiary">
                    No organizations yet.
                  </Text>
                )}
            </div>
          </>
        )}

        {viewMode === 'user' && (
          <>
            {detailLoading && (
              <div className="mt-8 flex justify-center">
                <Loading className="h-10 w-10" />
              </div>
            )}
            {detailError && (
              <Text className="mt-4 text-red-600" variant="studio/body/md">
                {detailError}
              </Text>
            )}
            {userDetail && (
              <div className="mt-8 space-y-6">
                <Text as="h1" variant="studio/display/xl">
                  User:{' '}
                  {(userDetail.user as any)?.email ??
                    (userDetail.user as any)?.userID}
                </Text>
                <div className="rounded-lg border p-4">
                  <pre className="text-sm">
                    {JSON.stringify(userDetail.user, null, 2)}
                  </pre>
                </div>
                {userDetail.organization ? (
                  <div>
                    <Text
                      as="h2"
                      variant="studio/body/bold/md"
                      className="mb-2"
                    >
                      Organization
                    </Text>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        handleViewOrganization(
                          (userDetail.organization as any)?.organizationID,
                        )
                      }
                    >
                      View organization
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Text as="h2" variant="studio/body/bold/md">
                      User is not in an organization
                    </Text>
                    <div className="flex flex-col gap-2">
                      <Input
                        name="orgName"
                        placeholder="Organization name"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                      />
                      <Button
                        disabled={actionLoading}
                        onClick={() =>
                          handleCreateOrgForUser(
                            (userDetail.user as any)?.userID,
                          )
                        }
                      >
                        Create organization for user
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Input
                        name="addToOrgId"
                        placeholder="Organization ID to add to"
                        value={addToOrgId}
                        onChange={(e) => setAddToOrgId(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <select
                          value={addToOrgPlan}
                          onChange={(e) =>
                            setAddToOrgPlan(
                              e.target.value as 'free' | 'pro' | 'unlimited',
                            )
                          }
                        >
                          <option value="free">Free</option>
                          <option value="pro">Pro</option>
                          <option value="unlimited">Unlimited</option>
                        </select>
                        <select
                          value={addToOrgAccess}
                          onChange={(e) =>
                            setAddToOrgAccess(e.target.value as 'EDIT' | 'VIEW')
                          }
                        >
                          <option value="EDIT">EDIT</option>
                          <option value="VIEW">VIEW</option>
                        </select>
                      </div>
                      <Button
                        disabled={actionLoading}
                        onClick={() =>
                          handleAddUserToOrg((userDetail.user as any)?.userID)
                        }
                      >
                        Add to organization
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {viewMode === 'organization' && (
          <>
            {detailLoading && (
              <div className="mt-8 flex justify-center">
                <Loading className="h-10 w-10" />
              </div>
            )}
            {detailError && (
              <Text className="mt-4 text-red-600" variant="studio/body/md">
                {detailError}
              </Text>
            )}
            {organizationDetail && (
              <div className="mt-8 space-y-8">
                <Text as="h1" variant="studio/display/xl">
                  {organizationDetail.organization.organizationName ?? '—'}
                </Text>
                <div className="rounded-lg border p-4">
                  <Text as="h2" variant="studio/body/bold/md" className="mb-2">
                    Usage credits
                  </Text>
                  {(() => {
                    const o: AdminUserManagementOrganizationRecord =
                      organizationDetail.organization;
                    const orgId = o.organizationID ?? '';
                    const creditsParsed = parseInt(manualOrgCreditsToGrant, 10);
                    const canSubmitManualCredits =
                      !!orgId &&
                      !Number.isNaN(creditsParsed) &&
                      Number.isInteger(creditsParsed) &&
                      creditsParsed >= 1 &&
                      creditsParsed < 15000;
                    const rows: { label: string; value: string }[] = [
                      {
                        label: 'Available credits (included + purchased)',
                        value:
                          o.availableCredits != null
                            ? String(o.availableCredits)
                            : '—',
                      },
                      {
                        label: 'Current credit balance (included)',
                        value:
                          o.currentCreditBalance != null
                            ? String(o.currentCreditBalance)
                            : '—',
                      },
                      {
                        label: 'Purchased credits',
                        value:
                          o.purchasedCredits != null
                            ? String(o.purchasedCredits)
                            : '—',
                      },
                      {
                        label: 'Monthly credit refresh (included)',
                        value:
                          o.monthlyCreditRefreshAmount != null
                            ? String(o.monthlyCreditRefreshAmount)
                            : '—',
                      },
                      {
                        label: 'Cost per credit',
                        value: formatMoney(o.costPerCredit, 4),
                      },
                      {
                        label: 'Top-up charge amount (USD)',
                        value: formatMoney(o.topUpAmountUSD, 2),
                      },
                      {
                        label: 'Pending top-up PaymentIntent',
                        value:
                          o.pendingOrgCreditTopUpPaymentIntentId &&
                          o.pendingOrgCreditTopUpPaymentIntentId.length > 0
                            ? o.pendingOrgCreditTopUpPaymentIntentId
                            : '—',
                      },
                      {
                        label: 'Last applied top-up PaymentIntent',
                        value:
                          o.lastAppliedOrgCreditTopUpPaymentIntentId &&
                          o.lastAppliedOrgCreditTopUpPaymentIntentId.length > 0
                            ? o.lastAppliedOrgCreditTopUpPaymentIntentId
                            : '—',
                      },
                    ];
                    return (
                      <>
                        <dl className="mb-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                          {rows.map((row) => (
                            <div key={row.label}>
                              <Text
                                as="dt"
                                variant="studio/body/sm"
                                className="text-tertiary"
                              >
                                {row.label}
                              </Text>
                              <Text
                                as="dd"
                                variant="studio/body/bold/md"
                                className="text-primary break-all"
                              >
                                {row.value}
                              </Text>
                            </div>
                          ))}
                        </dl>
                        <Text
                          variant="studio/body/sm"
                          className="text-tertiary mb-2 block"
                        >
                          Grant included credits (adds to current credit
                          balance). Enter a whole number from 1 to 15000 per
                          request.
                        </Text>
                        <div className="flex max-w-md flex-wrap items-end gap-2">
                          <div className="min-w-48 flex-1">
                            <Input
                              name="manualOrgCreditsToGrant"
                              type="number"
                              min={1}
                              max={15000}
                              step={1}
                              placeholder="Credits to add"
                              value={manualOrgCreditsToGrant}
                              onChange={(e) =>
                                setManualOrgCreditsToGrant(e.target.value)
                              }
                              onKeyDown={(e) =>
                                e.key === 'Enter' &&
                                void handleGrantManualOrgCredits(orgId)
                              }
                            />
                          </div>
                          <Button
                            disabled={actionLoading || !canSubmitManualCredits}
                            variant="secondary"
                            onClick={() =>
                              void handleGrantManualOrgCredits(orgId)
                            }
                          >
                            Grant credits
                          </Button>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="rounded-lg border p-4">
                  <Text as="h2" variant="studio/body/bold/md" className="mb-2">
                    Organization info
                  </Text>
                  <pre className="text-sm">
                    {JSON.stringify(organizationDetail.organization, null, 2)}
                  </pre>
                </div>
                <div>
                  <Text as="h2" variant="studio/body/bold/md" className="mb-2">
                    Add member to this organization
                  </Text>
                  <Text variant="studio/body/sm" className="text-tertiary mb-3">
                    Enter an email address or user ID for a user who is not
                    already in another organization (and is on a free plan for
                    paid-seat flows).
                  </Text>
                  <div className="flex max-w-xl flex-col gap-2">
                    <Input
                      name="orgPageAddMemberTarget"
                      placeholder="Email or user ID (e.g. user-…)"
                      value={orgPageAddMemberTarget}
                      onChange={(e) =>
                        setOrgPageAddMemberTarget(e.target.value)
                      }
                      onKeyDown={(e) =>
                        e.key === 'Enter' && void handleAddUserToCurrentOrg()
                      }
                    />
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={addToOrgPlan}
                        onChange={(e) =>
                          setAddToOrgPlan(
                            e.target.value as 'free' | 'pro' | 'unlimited',
                          )
                        }
                      >
                        <option value="free">Free</option>
                        <option value="pro">Pro</option>
                        <option value="unlimited">Unlimited</option>
                      </select>
                      <select
                        value={addToOrgAccess}
                        onChange={(e) =>
                          setAddToOrgAccess(e.target.value as 'EDIT' | 'VIEW')
                        }
                      >
                        <option value="EDIT">EDIT</option>
                        <option value="VIEW">VIEW</option>
                      </select>
                    </div>
                    <Button
                      disabled={actionLoading || !orgPageAddMemberTarget.trim()}
                      onClick={() => void handleAddUserToCurrentOrg()}
                    >
                      Add to organization
                    </Button>
                  </div>
                </div>
                <div>
                  <Text as="h2" variant="studio/body/bold/md" className="mb-2">
                    Members
                  </Text>
                  <div className="rounded-lg border">
                    {organizationDetail.members.map((m) => (
                      <div
                        key={m.userID}
                        className="flex flex-wrap items-center justify-between gap-2 border-b p-3 last:border-b-0"
                      >
                        <span>
                          {m.firstName} {m.lastName} ({m.email}) - {m.planType}{' '}
                          - {m.accessLevel}
                        </span>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleViewUser(m.userID)}
                          >
                            View user
                          </Button>
                          <Button
                            variant="secondary-destructive"
                            size="sm"
                            disabled={actionLoading}
                            onClick={() =>
                              setMemberRemovePrompt({
                                userID: m.userID,
                                email: m.email,
                                displayLabel: `${m.firstName} ${m.lastName} (${m.email})`,
                              })
                            }
                          >
                            Remove from org
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Text as="h2" variant="studio/body/bold/md" className="mb-2">
                    Modify seat capacity
                  </Text>
                  <Text variant="studio/body/sm" className="text-tertiary mb-3">
                    Add or remove legacy pro/unlimited seat totals, or
                    team/workspace seats purchased (does not assign users). Use
                    a negative number to correct a mistake. Capacity cannot go
                    below current active seats.
                  </Text>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1">
                      <Text
                        as="label"
                        variant="studio/body/sm"
                        className="text-tertiary"
                      >
                        Seat type
                      </Text>
                      <select
                        value={addSeatPlan}
                        onChange={(e) =>
                          setAddSeatPlan(
                            e.target.value as 'pro' | 'unlimited' | 'team',
                          )
                        }
                      >
                        <option value="pro">Pro</option>
                        <option value="unlimited">Unlimited</option>
                        <option value="team">Team</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Text
                        as="label"
                        variant="studio/body/sm"
                        className="text-tertiary"
                      >
                        Seat delta
                      </Text>
                      <Input
                        name="addSeatCount"
                        type="number"
                        step={1}
                        placeholder="e.g. 5 or -2"
                        value={addSeatCount}
                        onChange={(e) => setAddSeatCount(e.target.value)}
                      />
                    </div>
                    <Button
                      disabled={actionLoading}
                      onClick={() =>
                        void handleModifyOrganizationSeatCapacity()
                      }
                    >
                      Apply
                    </Button>
                  </div>
                </div>
                <div>
                  <Text as="h2" variant="studio/body/bold/md" className="mb-2">
                    API Keys (Service Accounts)
                  </Text>
                  <div className="mb-4 flex gap-2">
                    <Input
                      name="serviceName"
                      placeholder="Service name"
                      value={serviceName}
                      onChange={(e) => setServiceName(e.target.value)}
                    />
                    <Button
                      disabled={actionLoading}
                      onClick={() =>
                        handleCreateServiceAccount(
                          organizationDetail.organization.organizationID ?? '',
                        )
                      }
                    >
                      Create API key
                    </Button>
                  </div>
                  <div className="rounded-lg border">
                    {organizationDetail.serviceAccounts.map((s) => {
                      const orgId =
                        organizationDetail.organization.organizationID ?? '';
                      const balanceDeltaStr = balanceDeltas[s.serviceID] ?? '';
                      const invalidAfterStr =
                        invalidAfterDates[s.serviceID] ?? '';
                      const balanceDelta = parseFloat(balanceDeltaStr);
                      const isBalanceInRange =
                        Number.isNaN(balanceDelta) ||
                        Math.abs(balanceDelta) <= 1000;
                      const hasBalanceChange =
                        !Number.isNaN(balanceDelta) &&
                        balanceDelta !== 0 &&
                        isBalanceInRange;
                      const hasInvalidAfterChange = invalidAfterStr.length > 0;
                      const canUpdate =
                        hasBalanceChange || hasInvalidAfterChange;

                      return (
                        <div
                          key={s.serviceID}
                          className="border-b p-4 last:border-b-0"
                        >
                          <div className="mb-3 flex flex-wrap gap-4">
                            <div>
                              <Text
                                as="span"
                                variant="studio/body/sm"
                                className="opacity-60"
                              >
                                Account balance
                              </Text>
                              <Text
                                as="p"
                                variant="studio/body/bold/md"
                                className="text-primary"
                              >
                                ${(s.accountBalanceUSD ?? 0).toFixed(2)} USD
                              </Text>
                            </div>
                            <div>
                              <Text
                                as="span"
                                variant="studio/body/sm"
                                className="opacity-60"
                              >
                                Created at
                              </Text>
                              <Text
                                as="p"
                                variant="studio/body/bold/md"
                                className="text-primary"
                              >
                                {s.createdAt
                                  ? new Date(s.createdAt).toLocaleString()
                                  : '—'}
                              </Text>
                            </div>
                            <div>
                              <Text
                                as="span"
                                variant="studio/body/sm"
                                className="opacity-60"
                              >
                                Invalid after
                              </Text>
                              <Text
                                as="p"
                                variant="studio/body/bold/md"
                                className="text-primary"
                              >
                                {s.invalidAfter
                                  ? new Date(s.invalidAfter).toLocaleString()
                                  : '—'}
                              </Text>
                            </div>
                            <div>
                              <Text
                                as="span"
                                variant="studio/body/sm"
                                className="opacity-60"
                              >
                                Last 4
                              </Text>
                              <Text
                                as="p"
                                variant="studio/body/bold/md"
                                className="text-primary font-mono"
                              >
                                {s.apiKeyLast4Digits || '—'}
                              </Text>
                            </div>
                          </div>
                          <div className="mb-3 flex flex-wrap items-end gap-2">
                            <div className="flex flex-col gap-1">
                              <Text
                                as="label"
                                variant="studio/body/sm"
                                className="opacity-60"
                              >
                                Adjust balance (+/- USD, max ±1000)
                              </Text>
                              <Input
                                name={`balanceDelta-${s.serviceID}`}
                                type="number"
                                step="0.01"
                                placeholder="e.g. 10 or -5"
                                value={balanceDeltaStr}
                                onChange={(e) =>
                                  setBalanceDeltas((prev) => ({
                                    ...prev,
                                    [s.serviceID]: e.target.value,
                                  }))
                                }
                              />
                              {balanceDeltaStr && !isBalanceInRange && (
                                <Text
                                  as="span"
                                  variant="studio/body/sm"
                                  className="text-red-600"
                                >
                                  Max ±1000 USD per adjustment
                                </Text>
                              )}
                            </div>
                            <div className="flex flex-col gap-1">
                              <Text
                                as="label"
                                variant="studio/body/sm"
                                className="opacity-60"
                              >
                                Set invalid after date
                              </Text>
                              <Input
                                name={`invalidAfter-${s.serviceID}`}
                                type="datetime-local"
                                value={invalidAfterStr}
                                onChange={(e) =>
                                  setInvalidAfterDates((prev) => ({
                                    ...prev,
                                    [s.serviceID]: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <Button
                              disabled={!canUpdate || actionLoading}
                              size="sm"
                              onClick={() =>
                                handleUpdateServiceAccount(
                                  s.serviceID,
                                  orgId,
                                  hasBalanceChange ? balanceDelta : undefined,
                                  hasInvalidAfterChange
                                    ? invalidAfterStr
                                    : undefined,
                                )
                              }
                            >
                              Update
                            </Button>
                            <Button
                              disabled={actionLoading}
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                handleDeleteServiceAccount(orgId, s.serviceID)
                              }
                            >
                              Delete API key
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                            <span>
                              <strong>Service ID:</strong> {s.serviceID}
                            </span>
                            {s.serviceName && (
                              <span>
                                <strong>Name:</strong> {s.serviceName}
                              </span>
                            )}
                            <span>
                              <strong>Access:</strong> {s.accessLevel}
                            </span>
                            {s.createdBy && (
                              <span>
                                <strong>Created by:</strong> {s.createdBy}
                              </span>
                            )}
                            {s.allowedOverdraftUSD != null && (
                              <span>
                                <strong>Overdraft:</strong> $
                                {s.allowedOverdraftUSD.toFixed(2)}
                              </span>
                            )}
                            {s.numParallelJobs != null && (
                              <span>
                                <strong>Parallel jobs:</strong>{' '}
                                {s.numParallelJobs}
                              </span>
                            )}
                            {s.topUpAmountUSD != null && (
                              <span>
                                <strong>Top-up amount:</strong> $
                                {s.topUpAmountUSD.toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <Modal
          open={memberRemovePrompt !== null}
          onOpenChange={(open) => {
            if (!open) setMemberRemovePrompt(null);
          }}
          title="Remove member from organization?"
          maxWidth={480}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                type="button"
                disabled={actionLoading}
                onClick={() => setMemberRemovePrompt(null)}
              >
                Cancel
              </Button>
              <Button
                variant="primary-destructive"
                type="button"
                disabled={actionLoading}
                onClick={() => void handleConfirmRemoveMember()}
              >
                Remove member
              </Button>
            </div>
          }
        >
          <Text variant="studio/body/md" className="text-primary">
            {memberRemovePrompt
              ? `This will remove ${memberRemovePrompt.displayLabel} from this organization, revoke org access, decrement paid seats if applicable, and set their plan to free.`
              : null}
          </Text>
        </Modal>
      </div>
    </div>
  );
}
